<template>
  <div class="container frozen" v-if="$can(['SuperAdmin', 'service', 'manager'])">
    <div class="frozen-head">
      <h4 class="is-size-6-mobile is-size-5-tablet">
        <i class="fal fa-snowflake"></i> {{ $t('frozenShipment.page_title') }}
      </h4>
      <span v-if="report" class="frozen-note">{{ $t('frozenShipment.as_of', {time: time(report.generated_at)}) }}</span>
    </div>
    <p class="frozen-note">{{ $t('frozenShipment.page_note', {days}) }}</p>

    <div class="box frozen-filters">
      <div class="buttons has-addons">
        <button
            v-for="key in scopes"
            :key="key"
            class="button is-small seg"
            :class="{'seg--on': scope === key}"
            @click="setScope(key)"
        >
          {{ $t('frozenShipment.scope_' + key) }}
          <strong v-if="report" class="seg__count">{{ fmt(report.scopes[key].total) }}</strong>
        </button>
      </div>
      <div class="select is-small">
        <select v-model.number="days" :aria-label="$t('frozenShipment.days_label')" @change="fetchData()">
          <option v-for="option in dayOptions" :key="option" :value="option">{{ $t('frozenShipment.days_option', {days: option}) }}</option>
        </select>
      </div>
      <div class="select is-small">
        <select v-model="status" :aria-label="$t('frozenShipment.status_label')" @change="fetchData()">
          <option value="">{{ $t('frozenShipment.status_all') }}</option>
          <option v-for="option in statusOptions" :key="option.id" :value="option.id">{{ statusName(option.name) }}</option>
        </select>
      </div>
      <div class="select is-small">
        <select v-model="state" :aria-label="$t('frozenShipment.state_label')" @change="fetchData()">
          <option value="">{{ $t('frozenShipment.state_all') }}</option>
          <option v-for="option in stateOptions" :key="option.id" :value="option.id">{{ stateName(option.id, option.name) }}</option>
        </select>
      </div>
      <div class="select is-small">
        <select v-model="bucket" :aria-label="$t('frozenShipment.age_label')" @change="stopPage = 1; closeStop()">
          <option value="">{{ $t('frozenShipment.age_all') }}</option>
          <option v-for="key in buckets" :key="key" :value="key">{{ $t('frozenShipment.age_' + key) }}</option>
        </select>
      </div>
      <span v-if="loading" class="frozen-note">{{ $t('frozenShipment.loading') }}</span>
    </div>

    <p v-if="scope === 'other'" class="frozen-note">{{ $t('frozenShipment.scope_note_other') }}</p>

    <div v-if="unavailable" class="notification is-warning is-light">
      <i class="fal fa-exclamation-triangle"></i> {{ $t('frozenShipment.unavailable_' + unavailable) }}
      <button class="button is-small" style="margin-left: 8px" @click="fetchData()">{{ $t('frozenShipment.retry') }}</button>
    </div>

    <div v-if="report" :class="{'frozen-busy': loading}">
      <div v-if="report.source_stale" class="notification is-warning is-light">
        <i class="fal fa-clock"></i>
        {{ $t('frozenShipment.source_stale', {time: time(report.source_latest_scan_at)}) }}
      </div>

      <div class="columns is-multiline is-variable is-2">
        <div v-for="key in visibleModes" :key="key" class="column is-12-mobile is-6-tablet" :class="modeColumn">
          <button
              class="mode-card"
              :class="{'mode-card--on': mode === key}"
              :aria-pressed="mode === key ? 'true' : 'false'"
              @click="toggleMode(key)"
          >
            <span class="mode-card__label">{{ $t('frozenShipment.mode_' + key) }}</span>
            <span class="mode-card__value">{{ fmt(report.modes[key][bucket || 'total']) }}</span>
            <span class="mode-card__hint">{{ $t('frozenShipment.mode_hint_' + key) }}</span>
            <!-- One bar per age, not a stacked bar: one bill can have parcels in two ages, so they are not parts of a whole. -->
            <span
                v-for="age in buckets"
                :key="age"
                class="age-row"
                :class="{'age-row--dim': bucket && bucket !== age}"
                :title="$t('frozenShipment.age_' + age) + ': ' + fmt(report.modes[key][age])"
            >
              <span class="age-row__label">{{ $t('frozenShipment.age_' + age) }}</span>
              <span class="age-row__track"><span class="age-row__bar" :class="'age-row__bar--' + age" :style="{width: barWidth(report.modes[key][age], report.modes[key].total)}"></span></span>
              <span class="age-row__value">{{ fmt(report.modes[key][age]) }}</span>
            </span>
          </button>
        </div>
      </div>

      <div class="box">
        <div class="stops-bar">
          <h5 class="section-title">{{ $t('frozenShipment.stops_title') }}</h5>
          <span v-if="mode" class="tag is-medium is-link is-light">
            {{ $t('frozenShipment.mode_' + mode) }}
            <button class="delete is-small" :aria-label="$t('frozenShipment.clear_filter')" @click="toggleMode(mode)"></button>
          </span>
          <div class="control has-icons-left stops-search">
            <input
                v-model.trim="search"
                class="input is-small"
                type="search"
                :aria-label="$t('frozenShipment.search_label')"
                :placeholder="$t('frozenShipment.search_label')"
                @input="stopPage = 1; closeStop()"
            >
            <span class="icon is-small is-left"><i class="fal fa-search"></i></span>
          </div>
          <span class="frozen-note stops-bar__total">{{ $t('frozenShipment.stops_total', {total: fmt(stopCount)}) }}</span>
        </div>
        <p class="frozen-note">{{ $t('frozenShipment.stops_note') }}</p>

        <div class="table-container">
          <table class="table is-fullwidth is-hoverable is-narrow stops-table">
            <thead>
              <tr>
                <th>{{ $t('frozenShipment.col_stop') }}</th>
                <th>{{ $t('frozenShipment.col_province') }}</th>
                <th>{{ $t('frozenShipment.col_mode') }}</th>
                <th v-for="age in buckets" :key="age" class="has-text-right">{{ $t('frozenShipment.age_' + age) }}</th>
                <th class="has-text-right">{{ $t('frozenShipment.col_total') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="stop in pagedStops">
                <tr :key="stopKey(stop)" class="stop-row" :class="{'stop-row--open': openStopKey === stopKey(stop)}" @click="toggleStop(stop)">
                  <td>
                    <a
                        role="button"
                        tabindex="0"
                        :aria-expanded="openStopKey === stopKey(stop) ? 'true' : 'false'"
                        @keydown.enter.prevent="toggleStop(stop)"
                    >
                      <i class="fal" :class="openStopKey === stopKey(stop) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                      <span v-if="stop.mode === 'in_transit'" class="stop-row__heading">{{ $t('frozenShipment.heading_to') }}</span>
                      {{ stopName(stop) }}
                    </a>
                    <span class="tag is-light stop-row__kind">{{ $t('frozenShipment.kind_' + stop.stop_kind) }}</span>
                  </td>
                  <td>{{ stop.province || '-' }}</td>
                  <td>{{ $t('frozenShipment.mode_' + stop.mode) }}</td>
                  <td v-for="age in buckets" :key="age" class="has-text-right" :class="{'cell-dim': bucket && bucket !== age}">{{ fmt(stop[age]) }}</td>
                  <td class="has-text-right"><strong>{{ fmt(stop.total) }}</strong></td>
                </tr>
                <tr v-if="openStopKey === stopKey(stop)" :key="stopKey(stop) + ':bills'" class="bills-row">
                  <td colspan="7" class="bills-cell">
                    <FrozenBills :stop="stop" :filters="billFilters" :stop-label="stopName(stop)"/>
                  </td>
                </tr>
              </template>
              <tr v-if="!filteredStops.length && !loading">
                <td colspan="7" class="has-text-centered">{{ $t('frozenShipment.stops_empty') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="stopPages > 1" class="frozen-pager">
          <button class="button is-small" :disabled="stopPage <= 1 || loading" @click="goStops(stopPage - 1)">{{ $t('frozenShipment.prev') }}</button>
          <span>{{ $t('frozenShipment.page_of', {page: stopPage, pages: stopPages}) }}</span>
          <button class="button is-small" :disabled="stopPage >= stopPages || loading" @click="goStops(stopPage + 1)">{{ $t('frozenShipment.next') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import FrozenBills from './components/FrozenBills';
import {fetchFrozenSummary, reasonOf} from '@/services/frozenShipmentService';

const SCOPES = ['within_vte', 'prov_to_prov', 'other'];
const MODES = ['at_destination', 'in_transit', 'holding', 'packing'];
const BUCKETS = ['h24_48', 'h48_72', 'h72_plus'];
const STOPS_PER_PAGE = 50;

export default {
  name: 'FrozenShipments',
  components: {FrozenBills},
  data() {
    return {
      scopes: SCOPES,
      buckets: BUCKETS,
      dayOptions: [2, 7, 14, 30],
      scope: 'within_vte',
      days: 14,
      status: '',
      state: '',
      bucket: '',
      mode: '',
      search: '',
      stopPage: 1,
      openStopKey: null,
      report: null,
      statusOptions: [],
      stateOptions: [],
      loading: false,
      unavailable: null,
      requestSeq: 0,
    };
  },
  computed: {
    // The modes never merge: "other" only appears when a scan type outside the four exists.
    visibleModes() {
      if (!this.report) return MODES;
      return this.report.modes.other.total > 0 || this.mode === 'other' ? [...MODES, 'other'] : MODES;
    },
    modeColumn() {
      return this.visibleModes.length > 4 ? 'is-one-fifth-desktop' : 'is-3-desktop';
    },
    metric() {
      return this.bucket || 'total';
    },
    filteredStops() {
      if (!this.report) return [];
      const needle = this.search.toLowerCase();
      return this.report.stops
          .filter((stop) => stop[this.metric] > 0)
          .filter((stop) => !this.mode || stop.mode === this.mode)
          .filter((stop) => !needle || `${this.stopName(stop)} ${stop.province || ''}`.toLowerCase().includes(needle))
          .sort((a, b) => b[this.metric] - a[this.metric]);
    },
    // One place can hold bills in two modes, so it can take two rows but stays one stop.
    stopCount() {
      return new Set(this.filteredStops.map((stop) => `${stop.stop_kind}:${stop.stop_id}`)).size;
    },
    stopPages() {
      return Math.max(1, Math.ceil(this.filteredStops.length / STOPS_PER_PAGE));
    },
    pagedStops() {
      const start = (this.stopPage - 1) * STOPS_PER_PAGE;
      return this.filteredStops.slice(start, start + STOPS_PER_PAGE);
    },
    billFilters() {
      return {scope: this.scope, days: this.days, status: this.status, state: this.state, bucket: this.bucket};
    },
  },
  mounted() {
    if (this.$can(['SuperAdmin', 'service', 'manager'])) this.fetchData();
  },
  methods: {
    async fetchData() {
      const seq = ++this.requestSeq;
      this.loading = true;
      this.stopPage = 1;
      this.closeStop();
      try {
        const params = {scope: this.scope, days: this.days};
        if (this.status !== '') params.status = this.status;
        if (this.state !== '') params.state = this.state;
        const report = await fetchFrozenSummary(params);
        if (seq !== this.requestSeq) return;
        // Dimmed numbers cannot be clicked, but a keyboard can still reach them while loading.
        this.stopPage = 1;
        this.closeStop();
        this.report = report;
        this.statusOptions = report.statuses;
        this.stateOptions = report.states;
        this.unavailable = null;
      } catch (e) {
        if (seq !== this.requestSeq) return;
        this.report = null;
        this.unavailable = reasonOf(e);
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    setScope(scope) {
      if (this.scope === scope) return;
      this.scope = scope;
      this.fetchData();
    },
    toggleMode(mode) {
      this.mode = this.mode === mode ? '' : mode;
      this.stopPage = 1;
      this.closeStop();
    },
    stopKey(stop) {
      return `${stop.mode}:${stop.stop_kind}:${stop.stop_id}`;
    },
    stopName(stop) {
      return stop.stop_name || this.$t('frozenShipment.stop_unknown');
    },
    toggleStop(stop) {
      if (this.loading) return;
      const key = this.stopKey(stop);
      this.openStopKey = this.openStopKey === key ? null : key;
    },
    closeStop() {
      this.openStopKey = null;
    },
    goStops(page) {
      this.stopPage = page;
      this.closeStop();
    },
    barWidth(value, total) {
      return total > 0 ? `${Math.min(100, (value / total) * 100)}%` : '0%';
    },
    statusName(name) {
      return this.$te('common.' + name) ? this.$t('common.' + name) : name;
    },
    stateName(id, name) {
      return this.$te('frozenShipment.state_' + id) ? this.$t('frozenShipment.state_' + id) : name;
    },
    fmt(value) {
      return new Intl.NumberFormat('en-US').format(value || 0);
    },
    time(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';
    },
  },
};
</script>

<style lang="scss" scoped>
.frozen-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.frozen-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;

  .buttons,
  .buttons .button {
    margin-bottom: 0;
  }

  .select {
    flex: 0 0 auto;
    width: auto;

    select {
      width: auto;
    }
  }
}

// While a new filter loads, the old numbers stay visible but cannot be clicked: a click would apply them to the new filter.
.frozen-busy {
  opacity: 0.55;
  pointer-events: none;
}

.frozen-note {
  font-size: 0.8rem;
  color: #52514e;
  margin-bottom: 8px;
  text-align: left;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #0b0b0b;
  margin-bottom: 0;
  text-align: left;
}

.seg {
  background: #ffffff;
  color: #52514e;
  border-color: #c3c2b7;

  &--on {
    background: #2a78d6;
    border-color: #2a78d6;
    color: #ffffff;

    .seg__count {
      color: #ffffff;
    }
  }
}

.seg__count {
  margin-left: 6px;
}

.mode-card {
  display: block;
  width: 100%;
  height: 100%;
  padding: 12px 14px;
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #e1e0d9;
  border-radius: 6px;

  &:hover,
  &:focus-visible {
    border-color: #2a78d6;
  }

  &--on {
    border-color: #2a78d6;
    box-shadow: inset 0 0 0 1px #2a78d6;
  }
}

.mode-card__label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0b0b0b;
}

.mode-card__value {
  display: block;
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.3;
  color: #0b0b0b;
}

.mode-card__hint {
  display: block;
  min-height: 2.4em;
  margin-bottom: 8px;
  font-size: 0.75rem;
  line-height: 1.2;
  color: #52514e;
}

.age-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 0.72rem;
  color: #52514e;

  &--dim {
    opacity: 0.4;
  }
}

.age-row__label {
  flex: 0 0 72px;
  white-space: nowrap;
}

.age-row__track {
  flex: 1 1 auto;
  height: 8px;
  overflow: hidden;
  background: #f3f2ee;
  border-radius: 4px;
}

.age-row__bar {
  display: block;
  height: 100%;
  border-radius: 0 4px 4px 0;

  // One blue, older is darker. The labels and numbers beside each bar carry the same information.
  &--h24_48 {
    background: #86b6ef;
  }

  &--h48_72 {
    background: #2a78d6;
  }

  &--h72_plus {
    background: #104281;
  }
}

.age-row__value {
  flex: 0 0 auto;
  min-width: 42px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: #0b0b0b;
}

.stops-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.stops-search {
  flex: 0 1 240px;
}

.stops-bar__total {
  margin: 0 0 0 auto;
}

.stops-table {
  font-size: 0.88rem;

  th,
  td {
    text-align: left;
  }

  th.has-text-right,
  td.has-text-right {
    font-variant-numeric: tabular-nums;
    text-align: right;
  }
}

.stop-row {
  cursor: pointer;

  a {
    font-weight: 600;
  }

  .fal {
    width: 12px;
    margin-right: 2px;
    font-size: 0.7rem;
  }

  &--open td {
    background: #f3f2ee;
  }
}

.stop-row__heading {
  font-weight: 400;
  color: #52514e;
}

.stop-row__kind {
  height: 1.5em;
  margin-left: 6px;
  font-size: 0.68rem;
}

.cell-dim {
  opacity: 0.4;
}

// max-width: 0 keeps the wide bill list from stretching this table: the list scrolls inside its own container.
.bills-cell {
  max-width: 0;
  padding: 10px 12px 14px;
  background: #fafaf8;
}

.frozen-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  font-size: 0.85rem;
}
</style>
